import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import UIStepIndicator from 'UIComponents/connectedStep/UIStepIndicator';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIDropdownDivider from 'UIComponents/dropdown/UIDropdownDivider';
import { H7 } from 'UIComponents/elements/headings';
import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UIFlex from 'UIComponents/layout/UIFlex';
import { FormattedMessage } from '../../../components/I18n';
import { theme } from '../../../utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import { usePlaylist } from '../../savedItems';
import { useAllProgress } from '../../userProgress';
import { useIncorrectPlaylistRedirect } from '../hooks';
const StepIndicator = styled(UIStepIndicator).withConfig({
  displayName: "PlaylistNavigationDropdown__StepIndicator",
  componentId: "az0hv7-0"
})(["[class*='step-indicator__item']{padding-bottom:4px !important;}[class*='step-indicator__line']{z-index:1 !important;}"]);
const PlaylistItem = styled.span.withConfig({
  displayName: "PlaylistNavigationDropdown__PlaylistItem",
  componentId: "az0hv7-1"
})(["font-size:14px;font-weight:400;text-transform:none;p{position:relative;z-index:1;}&.selected::before{z-index:0;content:'';display:block;position:absolute;width:calc(100% + 32px);height:100%;background-color:", ";top:-10px;left:-16px;}"], theme.colors.CALYPSO_LIGHT);

const PlaylistNavigationDropdown = ({
  playlistId,
  buttonTitle,
  trackId,
  lessonId
}) => {
  const {
    playlist,
    playlistLoading,
    playlistError
  } = usePlaylist(Number(playlistId));
  const {
    allProgress
  } = useAllProgress();
  const history = useHistory();
  useIncorrectPlaylistRedirect(playlistId, trackId, lessonId);
  const StepIcon = useCallback(({
    index,
    stepIndex,
    defaultIcon
  }) => {
    if (!playlist || !allProgress) return defaultIcon;
    const currentItem = playlist.items[index];
    let status = 'NOT_STARTED';
    const trackProgress = allProgress.assetProgress.find(asset => asset.type === currentItem.type && asset.id === currentItem.itemId);

    if (trackProgress) {
      status = trackProgress.status;
    }

    const isActive = index === stepIndex;
    const isCompleted = status === 'COMPLETED';
    const iconName = isCompleted ? 'success' : 'blank';
    let backgroundColor = theme.colors.OLAF;
    let borderColor = theme.colors.BATTLESHIP;
    let color = theme.colors.OLAF;

    if (isActive) {
      backgroundColor = theme.colors.OLAF;
      color = isCompleted ? theme.colors.LORAX : theme.colors.OLAF;
      borderColor = theme.colors.LORAX;
    } else if (isCompleted) {
      backgroundColor = theme.colors.LORAX;
      color = theme.colors.OLAF;
      borderColor = theme.colors.LORAX;
    }

    return /*#__PURE__*/_jsx(UIIconCircle, {
      backgroundColor: backgroundColor,
      borderColor: borderColor,
      borderWidth: 2,
      color: color,
      name: iconName,
      size: 6
    });
  }, [allProgress, playlist]);
  if (playlistLoading || playlistError || !playlist) return null;
  return /*#__PURE__*/_jsx("div", {
    style: {
      marginBottom: -4
    },
    children: /*#__PURE__*/_jsx(UIDropdown, {
      buttonText: buttonTitle || playlist.name,
      buttonUse: "link",
      "data-test-id": "playlist-navigator",
      onOpenComplete: () => {
        UsageTracker.track('playlistInteraction', {
          action: 'navigator-dropdown-open',
          playlistId,
          trackId,
          lessonId
        });
      },
      placement: "top right",
      use: "unstyled",
      children: /*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        children: [/*#__PURE__*/_jsx(H7, {
          className: "m-x-4 m-y-4",
          children: playlist.name
        }), /*#__PURE__*/_jsx(UIDropdownDivider, {
          className: "m-top-0"
        }), /*#__PURE__*/_jsx(StepIndicator, {
          StepIconComponent: StepIcon,
          className: "m-x-4",
          direction: "vertical",
          onStepClick: index => {
            const navigateToItem = playlist.items[index];
            const navigateToPath = `/${navigateToItem.type === 'LESSON' ? 'lessons' : 'tracks'}/${navigateToItem.itemId}`;
            history.push({
              pathname: navigateToPath,
              search: history.location.search
            });
            UsageTracker.track('playlistInteraction', {
              action: 'navigator-dropdown-item-clicked',
              playlistId,
              trackId,
              lessonId,
              to: navigateToPath
            });
          },
          stepIndex: playlist.items.findIndex(item => [Number(trackId), Number(lessonId)].includes(item.itemId)),
          stepNames: playlist.items.map(item => /*#__PURE__*/_jsx(PlaylistItem, {
            className: [Number(trackId), Number(lessonId)].includes(item.itemId) ? 'selected' : '',
            "data-test-id": "playlist-navigator-item",
            "data-test-item-id": item.itemId,
            "data-test-item-type": item.type,
            children: /*#__PURE__*/_jsx("p", {
              children: item.name
            })
          }, item.name))
        }), /*#__PURE__*/_jsx(UIDropdownDivider, {
          style: {
            marginTop: -10,
            marginBottom: 0
          }
        }), /*#__PURE__*/_jsx(UIRouterLink, {
          className: "m-x-4 m-y-2",
          onClick: () => {
            UsageTracker.track('playlistInteraction', {
              action: 'navigator-dropdown-view-playlist-clicked',
              playlistId,
              trackId,
              lessonId
            });
          },
          to: `/saved-items/playlists/${playlistId}`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.playlists.viewPlaylist"
          })
        })]
      })
    })
  });
};

export default PlaylistNavigationDropdown;